import { Button, QuestRoutine, UserRole } from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"

import { useQuestDndSectionsStore } from "./hooks/useQuestDndSectionsStore.ts"
import { Toasts } from "../../../components/toaster/Toaster.tsx"
import { QUERY_KEYS } from "../../../constants.tsx"
import { useAllQuestsQuery } from "../../../networking/queries.ts"
import { useReorderQuestsMutation } from "../../../networking/quests/mutations.ts"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics.ts"
import { queryClient } from "../../../util/queryClient.ts"
import { useQBDatePickerStore } from "../QuestBoardDatePicker.tsx"
import useQuestBoardStore from "../useQuestBoardStore.ts"

const SaveDndQuestOrderButton = () => {
  const { data: quests } = useAllQuestsQuery()
  const { sections, userHasMadeChanges } = useQuestDndSectionsStore()
  const { selectedUserId } = useQuestBoardStore()
  const [isLoadingQuestBoard, setIsLoadingQuestBoard] = useState(false)
  const reorderQuestsMutation = useReorderQuestsMutation()
  const { questBoardDate } = useQBDatePickerStore()

  useEffect(() => {
    document.getElementById("quests-dnd-list")?.scrollTo(0, 0)
  }, [selectedUserId])

  const onSave = async () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.REORDER_QUESTS_SAVE)

    const newOrders = [
      ...sections[0][1].map((quest) => ({
        id: quest.id,
        order: quest.order,
        routine: QuestRoutine.MORNING,
        role: UserRole.PARENT,
      })),
      ...sections[1][1].map((quest) => ({
        id: quest.id,
        order: quest.order,
        routine: QuestRoutine.AFTERNOON,
        role: UserRole.PARENT,
      })),
      ...sections[2][1].map((quest) => ({
        id: quest.id,
        order: quest.order,
        routine: QuestRoutine.NIGHT,
        role: UserRole.PARENT,
      })),
      ...sections[3][1].map((quest) => ({
        id: quest.id,
        order: quest.order,
        routine: QuestRoutine.ANYTIME,
        role: UserRole.PARENT,
      })),
    ]

    const newOrdersWithGroupedQuests: typeof newOrders = []

    // we need to include the remaining grouped quests (all will have same order)
    newOrders.forEach((orderedQuest) => {
      const questSeries = quests?.find((quest) => quest.id === orderedQuest.id)
      const questGroupId = questSeries?.quest_group_id || null

      const questGroupSeries =
        questGroupId === null || !quests
          ? []
          : quests.filter((quest) => quest.quest_group_id === questGroupId)

      if (questGroupSeries.length > 1) {
        newOrdersWithGroupedQuests.push(
          ...questGroupSeries.map((series) => ({
            id: series.id,
            order: orderedQuest.order,
            routine: orderedQuest.routine,
            role: UserRole.PARENT,
          }))
        )
      } else newOrdersWithGroupedQuests.push(orderedQuest)
    })

    await reorderQuestsMutation.mutateAsync(newOrdersWithGroupedQuests)
    setIsLoadingQuestBoard(true)
    await Promise.all([
      queryClient.invalidateQueries([
        QUERY_KEYS.QUEST_BOARD,
        dayjs(questBoardDate).format("YYYY-MM-DD"),
      ]),
      queryClient.invalidateQueries([QUERY_KEYS.ALL_QUESTS]),
    ])
    setIsLoadingQuestBoard(false)
    toast.success(Toasts.successReorderingQuests, { duration: 1500 })
  }

  if (!userHasMadeChanges) return null

  return (
    <div className="px-4 py-3 w-full">
      <Button
        buttonType="primary"
        text="Save Order"
        onClick={onSave}
        fullWidth
        isLoading={reorderQuestsMutation.isLoading || isLoadingQuestBoard}
      />
    </div>
  )
}

export default SaveDndQuestOrderButton
